import React from 'react';
import { useHead, escapeAttr } from 'src/core/engine/head';
import { useQuery } from '@apollo/client';
import { NotFound } from 'src/components/NotFound/NotFound';
import { useSetResolved } from 'src/core/engine/router/index';
import { ProductPageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { ProductOptionsProvider } from 'src/components/ProductDetail/ProductOptionsProvider';
import { useUrlForPath } from 'src/hooks/useUrlForDomain';
import { useDeps } from 'src/hooks/useDeps';
import { absoluteUrl } from 'src/util/absoluteUrl';
import { useIndexSku } from 'src/hooks/useIndexSku';
import { KEYWORDS } from 'src/constants/meta';
import { AggregationQueryProvider } from 'src/components/CategoryListingPage/AggregationQueryProvider';
import { useSetPopupPageProduct } from 'src/components/PopupBanner/PopupPageContext';

import productQuerySlim from '../../queries/productDetailSlim.graphql';
//import categoryFaqQuery from '../../queries/categoryFaq.graphql';
import taxFreeSettingsQuery from '../../queries/getTaxFreeSettings.graphql';
import { taxFreeSettings } from '../../queries/__generated__/taxFreeSettings';
import { productDetail, productDetailVariables } from '../../queries/__generated__/productDetail';
//import { categoryFaq, categoryFaqVariables } from '../../queries/__generated__/categoryFaq';
import { ProductDetail } from '../../components/ProductDetail/ProductDetail';
import { useInitialGalleryMedia } from '../../components/ProductDetail/hooks/useInitialGalleryMedia';
import { cleanProduct, Product } from '../../components/ProductDetail/utils/product-detail.types';
import { ProductFaq, ProductFaqItem } from '../../components/ProductDetail/ProductFaq';
import { ProductShopByCategory } from '../../components/ProductDetail/ProductShopByCategory';
import { ErrorComponent } from '../../components/Error/ErrorComponent';
import { useCurrencyChange } from '../../components/AppShell/ConfigProvider';
import { Container, Wrapper } from '../../components/Layout';

import '../../scss/_root-component.scss';

type ProductRootProps = {
    pathname: string;
    id: number;
};

export type isVatEnabledForCategoryType = boolean | undefined | null;

export const ProductRoot: React.FC<ProductRootProps> = (props) => {
    useSetResolved();
    return (
        <ProductOptionsProvider pathname={props.pathname}>
            <ProductDetailInner pathname={props.pathname} id={props.id} />
        </ProductOptionsProvider>
    );
};

const ProductDetailInner: React.FC<ProductRootProps> = React.memo((props) => {
    /**
     * Make the initial Product Query
     */

    const { loading, error, data, refetch } = useQuery<productDetail, productDetailVariables>(productQuerySlim, {
        variables: { ids: [props.id] },
        ssr: useDeps().env.SSR_GQL,
    });
    const productFaq = parseProductFaq(data?.productDetail?.[0]?.faq);
    //const breadcrumbCategoryId = data?.productDetail?.[0]?.categories?.[0]?.id;
    //const shouldFetchCategoryFaq = productFaq?.length === 0 && Boolean(breadcrumbCategoryId);

    // const { data: categoryFaqData } = useQuery<categoryFaq, categoryFaqVariables>(categoryFaqQuery, {
    //     variables: { id: breadcrumbCategoryId || 0 },
    //     skip: !shouldFetchCategoryFaq,
    //     ssr: useDeps().env.SSR_GQL,
    // });

    const {
        loading: taxFreeSettingsLoading,
        error: taxFreeSettingsError,
        data: taxFreeSettingsData,
    } = useQuery<taxFreeSettings>(taxFreeSettingsQuery, {
        ssr: useDeps().env.SSR_GQL,
    });

    /**
     * if the currency changes, re-fire the query for the product
     */
    useCurrencyChange(refetch);

    /**
     * Loading handler
     */
    if (loading || taxFreeSettingsLoading)
        return (
            <Wrapper variant="page-inner">
                <Container variant="mobile-medium-padding">
                    <ProductPageSkeleton />
                </Container>
            </Wrapper>
        );

    /**
     * Error handling
     */
    if (error || taxFreeSettingsError) {
        return <ErrorComponent message={error?.message} />;
    }
    if (!data || !taxFreeSettingsData) {
        return <NotFound />;
    }
    if (!Array.isArray(data.productDetail)) {
        return <NotFound />;
    }

    const first = data.productDetail[0];

    if (!first) {
        return <NotFound />;
    }

    /**
     * Clean up of product data
     */
    const product = cleanProduct(first);

    if (product.margin_tier_prices[0]?.price <= 0) {
        return <NotFound />;
    }

    const isVatEnabledForCategory =
        taxFreeSettingsData?.taxFreeSettings?.enabled &&
        data.productDetail[0]?.categories?.some((category) =>
            taxFreeSettingsData?.taxFreeSettings?.allowed_categories?.includes(category && category?.name),
        );
    //const fallbackCategoryFaq = parseProductFaq(categoryFaqData?.categoryList?.faq);
    //const faq = productFaq?.length > 0 ? productFaq : fallbackCategoryFaq;
    const faq = productFaq;

    return <Inner product={product} productFaq={faq} isVatEnabledForCategory={isVatEnabledForCategory} />;
});

const transformDescription = (product: Product, productName: string) =>
    product.meta_description_category
        ? product.meta_description_category
              .replace(/\{\{\s*product\s+name\s*\}\}/gi, productName)
              .replace(/\{\{\s*brand\s+name\s*\}\}/gi, transformBrandBadge(product.brand_badge))
        : product.meta_description
          ? product.meta_description
          : '';

const transformBrandBadge = (brandBadge: string) => {
    const brandBadgeArray = brandBadge.includes('-')
        ? brandBadge.toLowerCase().split('-')
        : brandBadge.toLowerCase().split(' ');

    return brandBadgeArray.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const parseProductFaq = (rawFaq?: string | null): ProductFaqItem[] => {
    if (!rawFaq) {
        return [];
    }

    try {
        const parsedFaq = JSON.parse(rawFaq);

        return Array.isArray(parsedFaq) ? parsedFaq : [];
    } catch {
        return [];
    }
};

const Inner: React.FC<{
    product: Product;
    productFaq: ProductFaqItem[];
    isVatEnabledForCategory: isVatEnabledForCategoryType;
}> = ({ product, productFaq, isVatEnabledForCategory }) => {
    useSetPopupPageProduct(product.id, product.categories?.[0]?.id ?? null);

    const { DOMAIN } = useDeps().env;
    const url = useUrlForPath(product.url_key || '');
    const firstImage = useInitialGalleryMedia(product.media_entries)[0];
    const preload = firstImage && firstImage.medium_url;
    const productName = product.name || 'Product';
    const description = transformDescription(product, productName);

    const title = productName.toLowerCase().includes('workwear express')
        ? productName
        : `${productName} | Workwear Express`;

    const noIndexedProduct = useIndexSku(product.sku);

    useHead({
        title: `<title>${escapeAttr(title)}</title>`,
        meta: [
            noIndexedProduct ? '<meta name="robots" content="noindex" />' : '',
            `<meta name="description" content="${escapeAttr(description)}" />`,
            `<meta property="og:title" content="${escapeAttr(product.name || '')}" />`,
            `<meta property="og:description" content="${escapeAttr(description)}" />`,
            `<meta property="og:image" content="${escapeAttr(absoluteUrl(DOMAIN!, product.small_image || ''))}" />`,
            `<meta name="keywords" content="${escapeAttr(product.meta_keywords || KEYWORDS)}" />`,
        ].join(''),
        link: [
            `<link rel="canonical" href="${escapeAttr(url)}" />`,
            preload ? `<link rel="preload" href="${escapeAttr(preload)}" as="image" />` : '',
        ]
            .filter(Boolean)
            .join(''),
    });

    return (
        <Wrapper bg="white" variant="widget-wrapper-padded">
            <AggregationQueryProvider id={product.categories?.[0]?.id || 1}>
                <Container>
                    <ProductDetail
                        product={product}
                        isVatEnabledForCategory={isVatEnabledForCategory}
                        beforeReviews={
                            <>
                                {productFaq.length > 0 && <ProductFaq faq={productFaq} />}
                                <ProductShopByCategory />
                            </>
                        }
                    />
                </Container>
            </AggregationQueryProvider>
        </Wrapper>
    );
};

export default ProductRoot;
